import { action, observable } from "mobx";
import { PayGateLanguageCode } from "./payGateLanguageCode.enum";
import {
  CALLBACK,
  CALLBACK_REGEX,
  CALLBACK_URL,
  HOSTNAME_REGEX,
  LANGUAGE_CODE,
  ORDER_ID_REGEX,
  PARENT_REGEX,
  PORT_REGEX,
  PROTOCOL_REGEX,
  REDIRECT_AFTER_CONFIRM_URL,
  REDIRECT_URL,
  SPINNER,
} from "./constants";
import LocalizationService from "services/localizationService";
import {
  persistCallbackDestination,
  SESSION_STORAGE_EXTERNAL_SERVICE_CALLBACK_DESTINATION_URL,
} from "utils/externalServiceUtils";
import PaymentRepository from "../../repositories/paymentRepository";
import GooglePayMetadata from "../../models/googlePayMetadata";
import EventBus, {
  getGooglePayMetadataFailure,
  getGooglePayMetadataSuccess,
  googlePayPaymentBrowserFingerprintLoaded,
  paymentFailed,
  submitGooglePayPaymentBrowserFingerprint,
  submitGooglePayPaymentDeferred,
  submitGooglePayPaymentFailure,
  submitGooglePayPaymentSuccess,
} from "../eventBus";
import EshopError from "../../models/eshopError";
import { PayGateCommonPaymentStatus } from "../../models/googlePayInitPaymentResponse";
import GooglePayProcessPaymentRequest from "models/googlePayProcessPaymentRequest";
import SignalRNotificationService from "services/signalR/notification/signalRNotificationService";
import { NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED, NOTIFICATION_SERVICE_START_FAILURE } from "repositories/order/constants";

const PAYMENT_GATE_CALLBACK_DESTINATION_PATH = "%PARENT%/status/%ORDER_ID%";

export default class PaymentService {
  @observable isFetchingGooglePayMetadata = false;
  @observable submittingGooglePayment = false;
  googlePayPoolingInterval: number;

  constructor(
    private paymentRepository: PaymentRepository,
    private notificationService: SignalRNotificationService,
    private localizationService: LocalizationService,
    private eventBus: EventBus
  ) {}

  @action.bound
  async getGooglePayMetadata() {
    if (this.isFetchingGooglePayMetadata) {
      return;
    }

    this.isFetchingGooglePayMetadata = true;

    const response = await this.paymentRepository.getGooglePayMetadata();

    this.isFetchingGooglePayMetadata = false;

    if (response instanceof GooglePayMetadata) {
      this.eventBus.publish(getGooglePayMetadataSuccess({ googlePayMetadata: response }));
    } else {
      this.eventBus.publish(getGooglePayMetadataFailure());
    }
  }

  @action.bound
  async submitGooglePayment(orderId: string, payload: google.payments.api.PaymentData) {
    if (this.submittingGooglePayment) {
      this.eventBus.publish(submitGooglePayPaymentFailure());
      return;
    }

    this.submittingGooglePayment = true;

    const redirectAfterConfirmationUrl = this.buildSpinnerUrl();
    const response = await this.paymentRepository.submitGooglePayment(orderId, redirectAfterConfirmationUrl, payload);

    if (response instanceof EshopError) {
      this.eventBus.publish(submitGooglePayPaymentFailure());
      return;
    }

    if (response.browserFingerprintActionUrl) {
      // Most of the time, BE sends a browser fingerprint that we need to display in a 1px iframe.
      this.eventBus.publish(submitGooglePayPaymentBrowserFingerprint({ url: response.browserFingerprintActionUrl }));
    } else {
      // BE sometimes doesn't have to send the fingerprint, and the payment should go right through.
      this.eventBus.publish(googlePayPaymentBrowserFingerprintLoaded());
    }

    if (response.paymentStatus != PayGateCommonPaymentStatus.IN_PROGRESS) {
      this.finalizeGooglePayPayment(response.paymentStatus);
      return;
    }
  }

  @action.bound
  confirmGooglePayPayment(orderId: string) {
    return this.notificationService
      .serviceStart()
      .then(async () => {
        if (!this.notificationService.connectionId) {
          this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_CONNECTION_ID_UNDEFINED }));
          return;
        }

        this.notificationService.subscribeToGetGooglePayStatus();

        const request = {
          language: window.navigator.language,
          javaScriptEnabled: true,
          javaEnabled: true,
          colorDepth: screen.colorDepth,
          screenWidth: Math.min(screen.width, 868),
          screenHeight: Math.min(screen.height, 500),
          timezoneOffset: new Date().getTimezoneOffset(),
        } as GooglePayProcessPaymentRequest;

        const response = await this.paymentRepository.submitGooglePayPaymentConfirmation(
          orderId,
          this.notificationService.connectionId,
          request
        );

        if (response instanceof EshopError) {
          this.eventBus.publish(submitGooglePayPaymentFailure());
          return;
        }

        // If we receive a 3D secure redirect URL, we should show a 3D Secure popup.
        if (response.action?.browserChallenge?.challengeUrl) {
          return response.action.browserChallenge.challengeUrl;
        }
      })
      .catch(() => {
        this.eventBus.publish(paymentFailed({ message: NOTIFICATION_SERVICE_START_FAILURE }));
      });
  }

  @action.bound
  finalizeGooglePayPayment(status: PayGateCommonPaymentStatus) {
    clearInterval(this.googlePayPoolingInterval);
    this.submittingGooglePayment = false;

    switch (status) {
      case PayGateCommonPaymentStatus.SUCCEEDED:
        this.eventBus.publish(submitGooglePayPaymentSuccess());
        break;
      case PayGateCommonPaymentStatus.DEFERRED:
        this.eventBus.publish(submitGooglePayPaymentDeferred());
        break;
      default:
        this.eventBus.publish(submitGooglePayPaymentFailure());
    }
  }

  @action.bound
  async payGateRedirect(orderId: string, parent: string) {
    persistCallbackDestination(
      PAYMENT_GATE_CALLBACK_DESTINATION_PATH.replace(PARENT_REGEX, parent).replace(ORDER_ID_REGEX, orderId)
    );
    window.location.href = await this.buildRedirectUrl(orderId);
  }

  public resetRedirect() {
    const redirect = sessionStorage && sessionStorage[SESSION_STORAGE_EXTERNAL_SERVICE_CALLBACK_DESTINATION_URL];
    if (redirect) {
      sessionStorage.removeItem(SESSION_STORAGE_EXTERNAL_SERVICE_CALLBACK_DESTINATION_URL);
    }
  }

  private buildRedirectUrl = async (orderId: string) => {
    const { paymentGateway } = await import("config");

    const url = new URL(REDIRECT_URL.replace(ORDER_ID_REGEX, orderId), paymentGateway);
    url.searchParams.append(LANGUAGE_CODE, this.localizedPayGateLanguageCode());
    url.searchParams.append(REDIRECT_AFTER_CONFIRM_URL, this.buildCallbackUrl(orderId));
    return url.toString();
  };

  private buildCallbackUrl = (orderId: string) =>
    CALLBACK_URL.replace(PROTOCOL_REGEX, window.location.protocol)
      .replace(HOSTNAME_REGEX, window.location.hostname)
      .replace(PORT_REGEX, window.location.port ? ":" + window.location.port : "")
      .replace(CALLBACK_REGEX, CALLBACK.replace(ORDER_ID_REGEX, orderId));

  // The spinner URL is used in the 3D secure popup of Google Pay right after confirming the 3D secure code.
  // This is used to signal that we are processing the payment. Usually it is shown only for 1-2 seconds max.
  private buildSpinnerUrl = () =>
    CALLBACK_URL.replace(PROTOCOL_REGEX, window.location.protocol)
      .replace(HOSTNAME_REGEX, window.location.hostname)
      .replace(PORT_REGEX, window.location.port ? ":" + window.location.port : "")
      .replace(CALLBACK_REGEX, SPINNER);

  private localizedPayGateLanguageCode() {
    switch (this.localizationService.locale) {
      case "cs":
        return PayGateLanguageCode.CS;
      case "en-GB":
      case "en":
        return PayGateLanguageCode.EN;
      case "de":
        return PayGateLanguageCode.DE;
      case "hu":
        return PayGateLanguageCode.HU;
      case "pl":
        return PayGateLanguageCode.PL;
      case "ru":
        return PayGateLanguageCode.RU;
      default:
        return PayGateLanguageCode.EN;
    }
  }
}
